import type React from "react";

import { hexToRgb } from "@/lib/color/hex-to-rgb";

export function stageBadgeStyle(color: string): React.CSSProperties {
  const rgb = hexToRgb(color);
  return {
    backgroundImage: `linear-gradient(180deg, rgba(${rgb}, 0) 0%, rgba(${rgb}, 0.5) 100%), linear-gradient(90deg, rgba(${rgb}, 0.15) 0%, rgba(${rgb}, 0.15) 100%)`,
    borderColor: `rgba(${rgb}, 0.1)`,
  };
}

export interface EducationEntry {
  school_name?: string;
  display_name?: string;
  school_domain?: string;
  degrees?: string[];
  majors?: string[];
}

/**
 * Exact en-US timestamp ("Jul 3, 2026, 12:00 PM") for the tooltip behind a
 * compact relative label, in the viewer's timezone.
 */
export function formatExactDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** The compact duration below with an "ago" suffix, e.g. "3d ago". */
export function formatRelativeDate(iso: string): string {
  const compact = formatStageDuration(iso);
  return compact === "Just now" ? compact : `${compact} ago`;
}

/**
 * Compact elapsed-duration label (no "ago" suffix) for the pipeline "In Stage"
 * column, e.g. "3d", "2w", "5h". Reads as a duration rather than a point in
 * time. Clock skew (a timestamp slightly in the future) collapses to "Just now".
 */
export function formatStageDuration(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}
